import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, ArrowLeft, ChevronRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { EstimateWidget } from "../components/EstimateWidget";
import { ChatWidget } from "../components/ChatWidget";
import { useEstimate } from "../context/EstimateContext";
import { useLenis } from "../hooks/useLenis";
import { SERVICE_CONTENT } from "../data/services";
import { COMPANY } from "../data/site";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const data = SERVICE_CONTENT[slug];
  const { openEstimate } = useEstimate();
  useLenis();

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : "";
    document.title = data.metaTitle;
    setMeta("description", data.metaDescription);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "service-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: data.title,
      provider: { "@type": "GeneralContractor", name: COMPANY.name, telephone: "+1-484-632-5956" },
      areaServed: { "@type": "AdministrativeArea", name: "Lehigh Valley, Pennsylvania" },
      description: data.metaDescription,
    });
    document.head.appendChild(ld);

    const faq = document.createElement("script");
    faq.type = "application/ld+json";
    faq.id = "faq-jsonld";
    faq.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (data.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(faq);
    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      setMeta("description", prevDesc);
      const s = document.getElementById("service-jsonld");
      if (s) s.remove();
      const fq = document.getElementById("faq-jsonld");
      if (fq) fq.remove();
    };
  }, [slug, data]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="bg-[var(--apex-bg)] min-h-screen">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-[84px]">
          <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
            <img src={data.hero} alt={`${data.title} by Apex Home Pro in the Lehigh Valley`} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--apex-primary)] via-[var(--apex-primary)]/70 to-[var(--apex-primary)]/30" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 h-full flex flex-col justify-end pb-10">
              <nav className="flex items-center gap-1.5 text-sm text-slate-300 mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight size={14} />
                <a href="/#services" className="hover:text-white transition-colors">Services</a>
                <ChevronRight size={14} />
                <span className="text-white">{data.title}</span>
              </nav>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--apex-accent)]">{data.overline}</p>
              <motion.h1
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white mt-3 max-w-3xl leading-[1.02]"
              >
                {data.title}
              </motion.h1>
              <p className="text-slate-300 mt-3">Serving {COMPANY.area}</p>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {data.intro.map((p, i) => (
              <p key={i} className={`text-lg leading-relaxed ${i === 0 ? "text-[var(--apex-primary)] font-medium" : "text-slate-600 mt-5"}`}>{p}</p>
            ))}

            {data.promo && (
              <div data-testid="service-promo" className="mt-6 rounded-md border-2 border-[var(--apex-accent)] bg-[var(--apex-accent)]/5 p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-[var(--apex-accent)]">★ Limited-time offer</div>
                <h3 className="font-display font-bold text-xl text-[var(--apex-primary)] mt-1">{data.promo.title}</h3>
                <p className="text-slate-600 mt-2">{data.promo.text}</p>
              </div>
            )}

            {data.flyer && (
              <a href={data.flyer} target="_blank" rel="noreferrer" data-testid="service-flyer" className="block mt-6 rounded-md overflow-hidden ring-1 ring-slate-200 hover:ring-2 hover:ring-[var(--apex-accent)] transition-all">
                <img src={data.flyer} alt={`${data.title} special offer flyer — Apex Home Pro`} className="w-full" loading="lazy" />
              </a>
            )}

            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">What's included</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {data.included.map((f) => (
                <div key={f} className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-sm bg-[var(--apex-accent)] text-white"><Check size={14} /></span>
                  <span className="text-slate-700">{f}</span>
                </div>
              ))}
            </div>

            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">Our process</h2>
            <div className="mt-6 space-y-6">
              {data.process.map((s, i) => (
                <div key={s.title} className="flex gap-5">
                  <span className="font-display font-black text-2xl text-slate-300 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display font-bold text-lg text-[var(--apex-primary)]">{s.title}</h3>
                    <p className="text-slate-600 mt-1">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {data.gallery?.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-12">
                {data.gallery.map((src) => (
                  <img key={src} src={src} alt={`${data.title} project by Apex Home Pro`} className="w-full h-48 sm:h-64 object-cover rounded-md ring-1 ring-slate-200" />
                ))}
              </div>
            )}

            <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--apex-primary)] mt-12">Frequently asked</h2>
            <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
              {data.faqs.map((f) => (
                <div key={f.q} className="py-5">
                  <h3 className="font-semibold text-[var(--apex-primary)]">{f.q}</h3>
                  <p className="text-slate-600 mt-2">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sticky CTA */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-md bg-[var(--apex-primary)] p-8 text-white">
              <h3 className="font-display font-black text-2xl">Get a free estimate</h3>
              <p className="text-slate-300 mt-2">Tell us about your {data.title.toLowerCase()} project and we'll follow up within one business day.</p>
              <button
                data-testid="service-estimate-btn"
                onClick={openEstimate}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white font-semibold px-6 py-4 transition-colors"
              >
                Request Free Estimate <ArrowRight size={18} />
              </button>
              <a href={`tel:${COMPANY.phoneRaw}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 hover:border-white/50 font-semibold px-6 py-4 transition-colors">
                <Phone size={18} /> {COMPANY.phone}
              </a>
              <div className="mt-6 pt-6 border-t border-white/10 text-sm text-slate-300">
                <p className="font-semibold text-white">Licensed & insured</p>
                <p className="mt-1">Serving {COMPANY.area} · {COMPANY.hours}</p>
              </div>
            </div>
          </aside>
        </section>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-[var(--apex-accent)] font-semibold hover:gap-3 transition-all">
            <ArrowLeft size={18} /> Back to all services
          </Link>
        </div>
      </main>
      <Footer />
      <EstimateWidget />
      <ChatWidget />
    </div>
  );
}
